import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import cn from 'classnames';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import { ISortChange } from '../interfaces';
import styles from '../dataTable.module.scss';

interface IProps {
  sortable?: boolean,
  sortBy?: string,
  sortOrder?: string,
  tooltipContent?: string,
  tooltipMaxWidth?: string | number,
  onSortChange?: ISortChange,
  className?: string,
  name: string,
  title?: React.ReactNode,
  width?: string | number,
  maxWidth?: string | number,
  minWidth?: string | number,
  colSpan?: number,
  headCallback?: (props: IProps) => React.ReactNode | string,
  isVisible?: boolean,
  stickyRightPosition?: number,
  dataTest?: string,
}

const DraggableHeaderCell = (props: IProps) => {
  const {
    sortable = false,
    sortBy,
    sortOrder,
    tooltipContent,
    tooltipMaxWidth,
    onSortChange,
    className = '',
    name,
    title,
    width = 'auto',
    maxWidth,
    minWidth,
    colSpan = 1,
    headCallback = null,
    isVisible = true,
    stickyRightPosition,
    dataTest,
  } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: name });

  if (!isVisible) {
    return null;
  }

  const handleSortChange = () => (
    sortable && onSortChange(name, sortOrder === 'asc' ? 'desc' : 'asc', { title })
  );

  const renderContent = () => (
    typeof headCallback === 'function' ? headCallback(props) : title
  );

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
    ...(width && { width: typeof width === 'number' ? `${ width }px` : width }),
    ...(minWidth && { minWidth: typeof minWidth === 'number' ? `${ minWidth }px` : minWidth }),
    ...(maxWidth && { maxWidth: typeof maxWidth === 'number' ? `${ maxWidth }px` : maxWidth }),
    ...(stickyRightPosition && { right: `${ stickyRightPosition }px` }),
  };

  return (
    <th
      ref={ setNodeRef }
      style={ style }
      onClick={ handleSortChange }
      className={ cn(className, {
        [styles.isSortable]: sortable,
        [styles.isDraggingColumn]: isDragging,
      }) }
      colSpan={ colSpan }
      data-test={ dataTest || name }
      { ...attributes }
      { ...listeners }
    >
      <Tooltip
        content={ tooltipContent }
        disabled={ !tooltipContent }
        maxWidth={ tooltipMaxWidth }
      >
        <>
          { renderContent() }
          { sortable && (
            <span
              className={ cn(styles.sortable, {
                [styles.isAsc]: sortBy === name && sortOrder === 'asc',
                [styles.isDesc]: sortBy === name && sortOrder === 'desc',
              }) }
              data-test={ `${ name }SortButton` }
            />
          ) }
        </>
      </Tooltip>
    </th>
  );
};

export default DraggableHeaderCell;
